import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, SectionHeader } from "./ui";

const CAND_STATUS = ["nouveau", "vu", "retenu"];
const CAND_LABELS = { nouveau: "Nouveau", vu: "Vu", retenu: "Retenu" };

export default function MessagesAdmin() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("tous");
  const load = useCallback(() => { api.get("/admin/messages").then((r) => setItems(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);
  const markTraite = async (m) => { await api.patch(`/admin/messages/${m.id}`, { status: "traite" }); toast.success("Message traité"); load(); };
  const setCandStatus = async (m, status) => { await api.patch(`/admin/messages/${m.id}`, { status }); toast.success("Statut mis à jour"); load(); };
  const fmt = (d) => (d ? new Date(d).toLocaleDateString("fr-BE", { day: "numeric", month: "short", year: "numeric" }) : "-");
  const apiBase = api.defaults.baseURL;

  const FILTERS = [["tous", "Tous"], ...CAND_STATUS.map((s) => [s, CAND_LABELS[s]])];
  const isCand = (m) => (m.type || m.kind) === "candidature";
  const visible = items.filter((m) => filter === "tous" || (isCand(m) && (m.status || "nouveau") === filter));

  return (
    <Card>
      <SectionHeader title="Messages recus" />
      <div className="flex flex-wrap gap-2 mb-4" data-testid="admin-cand-filters">
        {FILTERS.map(([val, label]) => (
          <button key={val} onClick={() => setFilter(val)} data-testid={`admin-cand-filter-${val}`}
            className={`text-xs font-bold px-3 py-1.5 rounded-full transition-colors ${filter === val ? "bg-sky-500 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
            {label}
          </button>
        ))}
      </div>
      {visible.length === 0 ? <p className="text-slate-500">Aucun message.</p> : (
        <div className="space-y-3" data-testid="admin-messages-list">
          {visible.map((m) => (
            <div key={m.id} className="border border-slate-100 rounded-2xl p-4">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="font-bold text-slate-900">{m.subject} <span className="text-xs font-normal text-slate-400">({m.type || m.kind})</span></p>
                  <p className="text-xs text-slate-500">{m.name} · {m.email} · {fmt(m.created_at)}</p>
                </div>
                {isCand(m) ? (
                  <Select value={m.status || "nouveau"} onValueChange={(v) => setCandStatus(m, v)}>
                    <SelectTrigger className="rounded-xl w-36" data-testid={`admin-cand-status-${m.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{CAND_STATUS.map((s) => <SelectItem key={s} value={s}>{CAND_LABELS[s]}</SelectItem>)}</SelectContent>
                  </Select>
                ) : m.status === "traite" ? (
                  <span className="text-xs font-bold px-3 py-1 rounded-full bg-emerald-100 text-emerald-700">Traité</span>
                ) : (
                  <button onClick={() => markTraite(m)} data-testid={`admin-msg-treat-${m.id}`} className="text-xs font-bold px-4 py-2 rounded-full bg-sky-500 text-white hover:bg-sky-600 transition-colors">
                    Marquer traité
                  </button>
                )}
              </div>
              {m.message && <p className="text-sm text-slate-600 mt-2">{m.message}</p>}
              {m.cv_path && (
                <a href={`${apiBase}/candidatures/${m.id}/cv`} target="_blank" rel="noreferrer" data-testid={`admin-cv-${m.id}`} className="inline-flex items-center gap-1 text-xs font-bold text-violet-600 hover:underline mt-2">
                  Télécharger le CV {m.cv_filename ? `(${m.cv_filename})` : ""}
                </a>
              )}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
